/* eslint-disable max-lines */
import { AgGridModule } from '@ag-grid-community/angular';
import {
  ExcelExportParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowNode,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { DiscontinuationRateRadioComponent } from '@features/discontinuation-rate-radio/discontinuation-rate-radio.component';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { TrialSettingsService } from '@models/trial-settings/trial-settings.service';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  DefaultExpenseSource,
  EntityType,
  EventType,
  GqlService,
  PermissionType,
  TrialSettingKey,
  UpdateAccrualsInput,
  updateTrialSettingsMutation,
} from '@shared/services/gql.service';
import { injectFlags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { WorkflowStepType } from '@shared/store/workflow/workflow.const';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { AuxExcelStyles } from '@shared/utils';
import { ExportType, Utils } from '@shared/utils/utils';

import {
  AgExpenseDefaultGroupHeaderComponent,
  AgExpenseDefaultGroupHeaderComponentParams,
} from './ag-expense-default-group-header.component';
import { AgExpenseDefaultsDragComponent } from './state/ag-expense-defaults-drag/ag-expense-defaults-drag.component';
import { AgExpenseDefaultsHeaderComponent } from './state/ag-expense-defaults-header.component';
import { ExpenseDefaultsQuery } from './state/expense-defaults.query';
import { ExpenseDefaultsService } from './state/expense-defaults.service';
import { ExpenseSourceItems } from './state/expense-defaults.store';

const plannedVisitMethodologyMapping: { [key: string]: string } = {
  FIRST_VISIT_BASED: 'First Visit Based',
  LATEST_VISIT_BASED: 'Prior Visit Based',
};

const plannedVisitMethodologyReverseMapping: { [key: string]: string } = {
  'First Visit Based': 'FIRST_VISIT_BASED',
  'Prior Visit Based': 'LATEST_VISIT_BASED',
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    TablePanelComponent,
    AsyncPipe,
    ButtonComponent,
    ExportExcelButtonComponent,
    TooltipDirective,
    CheckboxComponent,
    FormsModule,
    AgGridModule,
    StickyGridDirective,
    NgSelectModule,
    ReactiveFormsModule,
    CommonModule,
    DiscontinuationRateRadioComponent,
  ],
  providers: [TrialSettingsService],
  selector: 'aux-expense-defaults',
  styleUrls: ['./expense-defaults.component.scss', './expense-defaults-table.scss'],
  templateUrl: './expense-defaults.component.html',
})
export class ExpenseDefaultsComponent {
  expenseDefaultsQuery = inject(ExpenseDefaultsQuery);
  expenseDefaultsService = inject(ExpenseDefaultsService);
  mainQuery = inject(MainQuery);
  organizationQuery = inject(OrganizationQuery);
  organizationService = inject(OrganizationService);
  overlayService = inject(OverlayService);
  trialsQuery = inject(TrialsQuery);

  private authService = inject(AuthService);
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private router = inject(Router);
  private trialSettingsService = inject(TrialSettingsService);
  private workflowQuery = inject(WorkflowQuery);

  flags = injectFlags({
    investigatorDiscontinuationRate: 'investigator_discontinuation_rate',
  });

  editMode = signal<boolean>(false);
  saveCheck = signal<boolean>(false);

  editButtonTooltip = computed(() => {
    const errArr: string[] = [];

    if (this.isWfLocked()) {
      errArr.push(
        'Vendor Expenses are locked for Period Close. Navigate to Period Close > Checklist and unlock to make changes.'
      );
    }

    if (!this.userHasModifyExpenseDefaultsPermission()) {
      errArr.push(
        'You do not have permission to perform this action. Please contact your system administrator if this has been restricted by mistake.'
      );
    }

    return errArr;
  });
  isWfLocked = computed(() => {
    const wf = this.workflowQuery.getWorkflowByStepType(
      WorkflowStepType.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
    );

    return wf()?.properties.locked || false;
  });

  userHasModifyExpenseDefaultsPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_MODIFY_EXPENSE_DEFAULTS],
    sysAdminsOnly: false,
  });

  isEditDisabled = computed(() => {
    return this.isWfLocked() || !this.userHasModifyExpenseDefaultsPermission();
  });
  isTableEditable = computed(() => {
    return !this.isEditDisabled() && this.editMode();
  });
  saveButtonTooltip = computed(() => {
    return this.saveCheck() ? '' : 'No changes to save.';
  });
  settingTooltip = computed(() => {
    return this.editMode() ? '' : 'Click edit to update.';
  });

  loading$ = combineLatest([
    this.organizationQuery.selectLoading(),
    this.expenseDefaultsQuery.selectLoading(),
  ]).pipe(
    map((x) => {
      return x.some((y) => y);
    })
  );

  discontinuationComponent = viewChild(DiscontinuationRateRadioComponent);

  _syncChangeDetectionWithDiscontinuationRateRadio = effect(() => {
    if (
      this.discontinuationComponent()?.initialDiscontinuationRate() !==
      this.discontinuationComponent()?.value()
    ) {
      this.saveCheck.set(true);
    } else {
      this.saveCheck.set(false);
    }
  });
  applyAutoHistoricalAdjustments = true;
  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  cloneRowData: ExpenseSourceItems[] = [];
  edits = new Set<DefaultExpenseSource>();
  excelOptions = {
    author: 'Auxilius',
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'co_name':
          return 300;
        default:
          return 200;
      }
    },
    fontSize: 11,
    sheetName: 'Expense Defaults',
    shouldRowBeSkipped(params) {
      return !params.node?.data?.organization_id;
    },
  } as ExcelExportParams;
  gridAPI!: GridApi;
  gridData$ = new BehaviorSubject<ExpenseSourceItems[]>([]);
  gridOptions: GridOptions = {
    ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
    autoGroupColumnDef: {
      cellRenderer: TableConstants.AG_SYSTEM.AG_GROUP_CELL_RENDERER,
      cellRendererParams: {
        suppressCount: true,
      },
      field: 'cost_category',
      headerClass: ['ag-header-align-center'],
      headerComponent: AgExpenseDefaultGroupHeaderComponent,
      headerComponentParams: {
        headerPage: 'expense_defaults',
        template: `Vendor Contract`,
      } as AgExpenseDefaultGroupHeaderComponentParams,
      headerName: 'Vendor Contract',
      minWidth: 200,
      pinned: 'left',
      resizable: true,
      showRowGroup: true,
      tooltipField: 'cat',
      tooltipValueGetter: (params) => {
        return params?.value || '';
      },
      valueFormatter: (x: ValueFormatterParams) => {
        return this.expenseDefaultsService.mapExpenseCategory.get(x?.value) || '';
      },
      width: 200,
    },
    columnDefs: [
      {
        field: 'cat',
        headerName: 'Cost Category',
        hide: true,
        resizable: false,
        tooltipField: 'cat',
        valueFormatter: (x: ValueFormatterParams) => {
          return this.expenseDefaultsService.mapExpenseCategory.get(x?.value) || '';
        },
      },
      {
        field: 'co_name',
        headerName: 'Vendor Contract',
        hide: true,
        resizable: false,
        rowGroup: true,
        tooltipField: 'co_name',
      },
      {
        cellRenderer: AgExpenseDefaultsDragComponent,
        cellRendererParams: {
          dragDropChangeFN: ({ rowNode }: { rowNode: RowNode }) => {
            this.compareToRow(rowNode.data);
          },
          editable: this.isTableEditable,
        },
        headerComponent: AgExpenseDefaultsHeaderComponent,
        minWidth: 680,
        resizable: false,
        width: 680,
      },
      {
        field: 'primary',
        headerName: 'Primary Source',
        hide: true,
        resizable: false,
      },
      {
        field: 'secondary',
        headerName: 'Secondary Source',
        hide: true,
        resizable: false,
      },
      {
        field: 'tertiary',
        headerName: 'Tertiary Source',
        hide: true,
        resizable: false,
      },
      {
        field: 'quaternary',
        headerName: 'Quaternary Source',
        hide: true,
        resizable: false,
      },
    ],
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      sortable: false,
    },
    excelStyles: [...AuxExcelStyles],
    groupDefaultExpanded: 1,
    suppressCellFocus: true,
    suppressColumnVirtualisation: true,
  };
  initalCheckBoxState = true;
  initialPlannedVisitMethodology = 'First Visit Based';
  plannedVisitMethodologyOptions = ['First Visit Based', 'Prior Visit Based'];
  selectedPlannedVisitMethodologyFormControl = new FormControl<string>('', {
    nonNullable: true,
  });
  width$ = new BehaviorSubject(0);

  async canDeactivate(): Promise<boolean> {
    if (this.saveCheck()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  compareToRow(row: DefaultExpenseSource) {
    if (this.edits.size > 0) {
      let att = 0;

      this.edits.forEach((edit) => {
        const changed = this.cloneRowData.find(
          (x) =>
            x.cost_category === edit.cost_category && x.organization_id === edit.organization_id
        );

        if (JSON.stringify(edit.sources) !== JSON.stringify(changed?.sources)) {
          att = att + 1;
        }

        if (
          edit.cost_category === row.cost_category &&
          edit.organization_id === row.organization_id
        ) {
          this.edits.delete(edit);
        }
      });

      if (att > 0) {
        this.saveCheck.set(true);

        this.edits.add({
          cost_category: row.cost_category,
          organization_id: row.organization_id,
          sources: row.sources,
        } as DefaultExpenseSource);
      } else {
        this.saveCheck.set(false);
        this.edits.clear();
      }
    } else {
      this.edits.add({
        cost_category: row.cost_category,
        organization_id: row.organization_id,
        sources: row.sources,
      } as DefaultExpenseSource);
      this.saveCheck.set(true);
    }
  }

  constructor() {
    this.organizationService.get().pipe(takeUntilDestroyed()).subscribe();

    this.expenseDefaultsService.getExpenseDefaults().pipe(takeUntilDestroyed()).subscribe();

    this.trialSettingsService
      .getAutoHistoricalAdjustmentSetting$()
      .pipe(takeUntilDestroyed())
      .subscribe((settingValue) => {
        this.initalCheckBoxState =
          settingValue === this.trialSettingsService.autoHistoricalAdjustmentBoxChecked;
        this.applyAutoHistoricalAdjustments = this.initalCheckBoxState;
      });

    this.trialSettingsService
      .getPlannedVisitMethodologySetting$()
      .pipe(takeUntilDestroyed())
      .subscribe(
        (settingValue) => (
          (this.initialPlannedVisitMethodology =
            plannedVisitMethodologyMapping[settingValue || 'FIRST_VISIT_BASED']),
          this.selectedPlannedVisitMethodologyFormControl.setValue(
            plannedVisitMethodologyMapping[settingValue || 'FIRST_VISIT_BASED']
          )
        )
      );

    this.organizationQuery
      .selectLoading()
      .pipe(
        takeUntilDestroyed(),
        switchMap((bool) => {
          if (bool) {
            return EMPTY;
          }

          return this.expenseDefaultsQuery.select();
        })
      )
      .subscribe(({ items }) => {
        if (items.length > 0) {
          const nItems = items
            .map((x) => {
              return {
                ...x,
                co_name: this.organizationQuery.getEntity(x.organization_id)?.name || '',
              };
            })
            .sort((x, y) => Utils.alphaNumSort(y?.cost_category, x?.cost_category))
            .sort((x, y) => Utils.alphaNumSort(x.co_name, y.co_name));

          this.gridData$.next(nItems || []);
          this.cloneRowData = Utils.clone(nItems);
        }
      });

    effect(() => {
      if (this.editMode()) {
        this.selectedPlannedVisitMethodologyFormControl.enable();
        this.discontinuationComponent()?.discontinuationRateRadioFc.enable();
      } else {
        this.selectedPlannedVisitMethodologyFormControl.disable();
        this.discontinuationComponent()?.discontinuationRateRadioFc.disable();
      }
    });
  }

  getDynamicExcelParams(): ExcelExportParams {
    const trial = this.trialsQuery.getEntity(this.mainQuery.getValue().trialKey);

    if (!trial) {
      return {};
    }

    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');

    return {
      ...this.excelOptions,
      columnKeys: ['co_name', 'cat', 'primary', 'secondary', 'tertiary', 'quaternary'],
      fileName: `${trial.short_name}_expense_defaults_${dateStr}.xlsx`,
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${trial.short_name}` },
              mergeAcross: 4,
              styleId: 'first_row',
            },
          ],
        },
      ],
    };
  }

  handleInMonthAdjustmentClick(): void {
    if (!this.launchDarklyService.$select((flags) => flags.tab_in_month_adjustments)()) {
      void this.router.navigateByUrl(
        `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CLOSE_SUMMARY}`
      );
    } else {
      void this.router.navigateByUrl(
        `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`
      );
    }
  }

  isBtnLoading(str: string) {
    return this.btnLoading$.pipe(map((x) => x === str));
  }

  onCancel(): void {
    this.onDiscardChanges();
    this.editMode.set(false);
  }

  onClickCheckbox(selected: boolean) {
    this.applyAutoHistoricalAdjustments = selected;

    if (this.initalCheckBoxState === this.applyAutoHistoricalAdjustments) {
      this.saveCheck.set(false);
    } else {
      this.saveCheck.set(true);
    }
  }

  onDiscardChanges() {
    this.discontinuationComponent()?.setDefaultDiscontinuationRate();
    this.saveCheck.set(false);
    this.gridData$.next(Utils.clone(this.cloneRowData));
    this.edits.clear();
    this.applyAutoHistoricalAdjustments = this.initalCheckBoxState;
    this.selectedPlannedVisitMethodologyFormControl.setValue(this.initialPlannedVisitMethodology);
  }

  onEditMode(): void {
    this.editMode.set(true);
  }

  onExportExpenseDefaults = async () => {
    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('export');

    const dateStr = dayjs().format('YYYY.MM.DD-HHmmss');
    const trial = this.mainQuery.getSelectedTrial();
    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: trial?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.EXPENSE_SOURCE_DEFAULTS,
          filename: `${trial?.name}_expense_defaults_${dateStr}`,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  };

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.refreshGridWidth();
  }

  onPlannedVisitMethodologySelect() {
    if (
      this.selectedPlannedVisitMethodologyFormControl.value !== this.initialPlannedVisitMethodology
    ) {
      this.saveCheck.set(true);
    } else {
      this.saveCheck.set(false);
    }
  }

  refreshGridWidth() {
    const columnContainer = document.querySelector('.ag-header-viewport') as HTMLDivElement;
    const columnRowContainer = document.querySelector('.ag-header-container') as HTMLDivElement;

    if (columnContainer && columnRowContainer && this.gridAPI) {
      const totalColumnWidth = columnContainer.getBoundingClientRect().width;
      const showedColumnWidth = columnRowContainer.getBoundingClientRect().width;
      const extraWidth = totalColumnWidth - showedColumnWidth;

      if (extraWidth) {
        this.resizeAutoGroupCol(extraWidth);
        this.width$.next(extraWidth);
      } else {
        this.width$.next(0);
      }
    }
  }

  resizeAutoGroupCol(extraWidth: number) {
    const autoGroupCol = this.gridAPI.getColumn('ag-Grid-AutoColumn');

    if (autoGroupCol) {
      const prevWidth = autoGroupCol.getActualWidth();

      this.gridAPI.setColumnWidth(autoGroupCol, prevWidth + extraWidth);
    }
  }

  // eslint-disable-next-line max-lines-per-function
  saveChanges = async () => {
    const saveData: DefaultExpenseSource[] = [];

    for (const edit of this.edits) {
      saveData.push(edit);
    }

    const vendorIds = this.gridData$
      .getValue()
      .map((data) => data.organization_id)
      .filter((organization_id) =>
        saveData.map((data) => data.organization_id).indexOf(organization_id)
      );
    let { success: checkboxSuccess }: GraphqlResponse<updateTrialSettingsMutation> = {
      data: null,
      errors: [],
      success: true,
    };
    let expenseSuccess = true;

    if (this.applyAutoHistoricalAdjustments !== this.initalCheckBoxState) {
      ({ success: checkboxSuccess } = await firstValueFrom(
        this.gqlService.updateTrialSettings$({
          setting_key: TrialSettingKey.TRIAL_SETTING_APPLY_AUTO_HISTORICAL_ADJUSTMENTS,
          setting_value: this.applyAutoHistoricalAdjustments ? 'TRUE' : 'FALSE',
        })
      ));

      if (checkboxSuccess) {
        this.initalCheckBoxState = this.applyAutoHistoricalAdjustments;

        const updateAccrualsInput: UpdateAccrualsInput = {
          accrual_inputs: vendorIds.map((organization_id) => ({
            organization_id,
          })),
        };

        await firstValueFrom(
          this.eventService.processEvent$({
            entity_id: '',
            entity_type: EntityType.TRIAL,
            payload: JSON.stringify(updateAccrualsInput),
            type: EventType.UPDATE_QUARTER_ACCRUALS,
          })
        );
      }
    }

    let { success: plannedVisitMethodologySuccess }: GraphqlResponse<updateTrialSettingsMutation> =
      {
        data: null,
        errors: [],
        success: true,
      };

    if (
      this.selectedPlannedVisitMethodologyFormControl.value !== this.initialPlannedVisitMethodology
    ) {
      ({ success: plannedVisitMethodologySuccess } = await firstValueFrom(
        this.gqlService.updateTrialSettings$({
          setting_key: TrialSettingKey.TRIAL_SETTING_PLANNED_VISIT_METHODOLOGY,
          setting_value:
            plannedVisitMethodologyReverseMapping[
              this.selectedPlannedVisitMethodologyFormControl.value
            ],
        })
      ));

      if (plannedVisitMethodologySuccess) {
        this.initialPlannedVisitMethodology = this.selectedPlannedVisitMethodologyFormControl.value;

        await firstValueFrom(
          this.eventService.processEvent$({
            entity_id: '',
            entity_type: EntityType.TRIAL,
            type: EventType.UPDATE_INVESTIGATOR_SCHEDULES,
          })
        );
      }
    }

    if (saveData.length > 0) {
      expenseSuccess = await this.expenseDefaultsService.updateExpenseDefaults(saveData);
    }

    await this.discontinuationComponent()?.updateDiscontinuedSetting();

    if (expenseSuccess && checkboxSuccess) {
      this.saveCheck.set(false);
      this.editMode.set(false);
      this.edits.clear();
      this.cloneRowData = Utils.clone(this.gridData$.getValue());
    }
  };
}
